import {
  Alert,
  Button,
  Checkbox,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Select,
  Space,
  Switch,
  Tag,
  TimePicker,
  Upload,
  message,
} from "antd";
import axios from "axios";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { serverUrl } from "../../../../../nestserver";
import { uploadDoc } from "../../../../../services/upload.service";
import useVoiceRecorder, { MAX_RECORDING_SECONDS } from "./useVoiceRecorder";

const { TextArea } = Input;

// 0 = Sunday … 6 = Saturday — matches Luxon's `weekday % 7` on the backend
// cron (see WakeupAlarmService.handleAlarmCron).
export const WEEKDAYS = [
  { label: "Sun", value: 0 },
  { label: "Mon", value: 1 },
  { label: "Tue", value: 2 },
  { label: "Wed", value: 3 },
  { label: "Thu", value: 4 },
  { label: "Fri", value: 5 },
  { label: "Sat", value: 6 },
];

/**
 * IANA zone names for the picker.
 *
 * `Intl.supportedValuesOf` is the browser's own copy of the tz database, which
 * is the same source the backend's timezone validator checks against — so a
 * zone offered here is always one the server will accept. The fallback covers
 * older browsers where the API is missing; it is deliberately short rather
 * than a stale bundled list.
 */
const TIMEZONES = (() => {
  try {
    const values = Intl.supportedValuesOf?.("timeZone");
    if (values?.length) return values;
  } catch (err) {
    // fall through
  }
  return [
    "Asia/Kolkata",
    "Asia/Dubai",
    "Asia/Colombo",
    "Asia/Singapore",
    "Europe/London",
    "America/New_York",
  ];
})();

const deviceTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Kolkata";
  } catch (err) {
    return "Asia/Kolkata";
  }
};

const formatSeconds = (total) => {
  const m = String(Math.floor(total / 60)).padStart(2, "0");
  const s = String(total % 60).padStart(2, "0");
  return `${m}:${s}`;
};

/**
 * One saved tone with its preview. `active` marks which of the two actually
 * rings — the precedence rule (uploaded file beats recording) is invisible
 * otherwise, and an admin whose recording is silently outranked would assume
 * the feature is broken.
 */
const TonePreview = ({ label, tone, active, onRemove }) => (
  <div
    className={`p-2 rounded border ${
      active ? "border-blue-400 bg-blue-50" : "border-gray-200"
    }`}
  >
    <div className="flex items-center justify-between mb-1">
      <span className="text-xs font-medium">
        {label}
        {active && <Tag color="blue" className="ml-2">Ringing</Tag>}
      </span>
      <Button type="link" danger size="small" onClick={onRemove}>
        Remove
      </Button>
    </div>
    <div className="text-xs text-gray-500 mb-1 truncate">{tone.name}</div>
    <audio controls src={tone.url} style={{ width: "100%" }} />
  </div>
);

const AddEditAlarmModal = ({ visible, onClose, onSaved, editing, classes }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [audio, setAudio] = useState(null);
  const [voice, setVoice] = useState(null);
  const [savingVoice, setSavingVoice] = useState(false);
  const [scope, setScope] = useState("school");
  const [recurrenceType, setRecurrenceType] = useState("weekdays");

  const recorder = useVoiceRecorder();
  // Pulled out so the effect below can depend on it: `reset` is a stable
  // useCallback, whereas `recorder` is a fresh object every render and would
  // re-run the effect forever.
  const { reset: resetRecorder } = recorder;

  // Mirrors WakeupAlarmModel.effectiveAudio on the device: uploaded file wins.
  const activeTone = audio ? "audio" : voice ? "voice" : null;

  useEffect(() => {
    if (!visible) return;

    resetRecorder();

    if (editing) {
      setScope(editing.scope);
      setRecurrenceType(editing.recurrence?.type || "weekdays");
      setAudio(editing.audio || null);
      setVoice(editing.voice || null);
      form.setFieldsValue({
        scope: editing.scope,
        class_ids: (editing.class_ids || []).map((c) => c?._id || c),
        title: editing.title,
        message: editing.message,
        at_time: editing.recurrence?.at_time
          ? moment(editing.recurrence.at_time, "HH:mm")
          : null,
        recurrence_type: editing.recurrence?.type || "weekdays",
        days_of_week: editing.recurrence?.days_of_week || [],
        snooze_minutes: editing.snooze_minutes ?? 5,
        max_snoozes: editing.max_snoozes ?? 3,
        skip_on_holiday: editing.skip_on_holiday ?? true,
        target_roles: editing.target_roles || ["student", "parent"],
        timezone: editing.timezone || "Asia/Kolkata",
        is_active: editing.is_active ?? false,
      });
    } else {
      setScope("school");
      setRecurrenceType("weekdays");
      setAudio(null);
      setVoice(null);
      form.setFieldsValue({
        scope: "school",
        class_ids: [],
        title: "Good morning!",
        message: "Time to wake up and get ready for school.",
        at_time: moment("06:30", "HH:mm"),
        recurrence_type: "weekdays",
        days_of_week: [1, 2, 3, 4, 5],
        snooze_minutes: 5,
        max_snoozes: 3,
        skip_on_holiday: true,
        target_roles: ["student", "parent"],
        timezone: deviceTimezone(),
        // New alarms start on. They used to be created inactive and silently
        // do nothing until someone noticed the switch in the table.
        is_active: true,
      });
    }
  }, [visible, editing, form, resetRecorder]);

  const handleAudioUpload = async (options) => {
    const { file } = options;

    if (!file.type?.startsWith("audio/")) {
      message.error("Please choose an audio file (mp3, m4a, wav).");
      return;
    }
    // The device downloads and caches this file; a large tone means a slow
    // first sync on a poor connection.
    if (file.size > 5 * 1024 * 1024) {
      message.error("Alarm audio must be 5 MB or smaller.");
      return;
    }

    setUploading(true);
    try {
      const result = await uploadDoc({ file }, "alarm-audio");
      setAudio({
        id: result.id,
        name: file.name,
        type: "AUDIO",
        url: result.files.original,
      });
      message.success("Alarm tone uploaded");
    } catch (err) {
      message.error("Failed to upload the alarm tone");
    } finally {
      setUploading(false);
    }
  };

  /// Uploads the recorded WAV through the same path an uploaded file takes.
  const handleSaveRecording = async () => {
    if (!recorder.result) return;
    setSavingVoice(true);
    try {
      const uploaded = await uploadDoc({ file: recorder.result.file }, "alarm-audio");
      setVoice({
        id: uploaded.id,
        name: recorder.result.file.name,
        type: "AUDIO",
        url: uploaded.files.original,
      });
      recorder.reset();
      message.success("Recording saved");
    } catch (err) {
      message.error("Failed to upload the recording");
    } finally {
      setSavingVoice(false);
    }
  };

  const handleSubmit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (err) {
      return;
    }

    const payload = {
      scope: values.scope,
      class_ids: values.scope === "class" ? values.class_ids : [],
      title: values.title,
      message: values.message,
      recurrence: {
        type: values.recurrence_type,
        at_time: values.at_time.format("HH:mm"),
        days_of_week:
          values.recurrence_type === "weekly" ? values.days_of_week : [],
      },
      timezone: values.timezone,
      snooze_minutes: values.snooze_minutes,
      max_snoozes: values.max_snoozes,
      skip_on_holiday: values.skip_on_holiday,
      target_roles: values.target_roles,
      is_active: values.is_active,
      // Sent explicitly as null when cleared, so removing a tone actually
      // removes it rather than the field being omitted and left as-is.
      audio: audio || null,
      voice: voice || null,
    };

    setSaving(true);
    try {
      const headers = {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      };
      if (editing) {
        await axios.patch(
          `${serverUrl}/wakeup-alarm/${editing._id}`,
          payload,
          { headers },
        );
        message.success("Alarm updated");
      } else {
        await axios.post(`${serverUrl}/wakeup-alarm`, payload, { headers });
        message.success("Alarm created");
      }
      onSaved();
      onClose();
    } catch (err) {
      message.error(
        err?.response?.data?.error?.errors?.[0] || "Failed to save the alarm",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={editing ? "Edit wake-up alarm" : "New wake-up alarm"}
      visible={visible}
      onCancel={onClose}
      width={640}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={saving}
          onClick={handleSubmit}
        >
          {editing ? "Save changes" : "Create alarm"}
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical">
        <Form.Item label="Applies to" name="scope">
          <Radio.Group onChange={(e) => setScope(e.target.value)}>
            <Radio.Button value="school">Whole school</Radio.Button>
            <Radio.Button value="class">Specific classes</Radio.Button>
          </Radio.Group>
        </Form.Item>

        {scope === "class" && (
          <Form.Item
            label="Classes"
            name="class_ids"
            rules={[
              { required: true, message: "Select at least one class" },
              {
                validator: (_, value) =>
                  value && value.length > 0
                    ? Promise.resolve()
                    : Promise.reject(new Error("Select at least one class")),
              },
            ]}
            extra="A class alarm replaces the school-wide alarm for those students, so nobody is woken twice."
          >
            <Select
              mode="multiple"
              placeholder="Choose classes"
              optionFilterProp="children"
              allowClear
            >
              {(classes || []).map((c) => (
                <Select.Option key={c._id} value={c._id}>
                  {c.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}

        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            label="Wake-up time"
            name="at_time"
            rules={[{ required: true, message: "Pick a time" }]}
          >
            <TimePicker format="HH:mm" minuteStep={5} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            label="Timezone"
            name="timezone"
            rules={[{ required: true, message: "Pick a timezone" }]}
            extra="The alarm rings at this time in this zone, wherever the family is."
          >
            <Select
              showSearch
              placeholder="Select timezone"
              optionFilterProp="value"
              options={TIMEZONES.map((tz) => ({ label: tz, value: tz }))}
            />
          </Form.Item>
        </div>

        <Form.Item label="Repeats" name="recurrence_type">
          <Radio.Group onChange={(e) => setRecurrenceType(e.target.value)}>
            <Radio.Button value="daily">Every day</Radio.Button>
            <Radio.Button value="weekdays">Mon–Fri</Radio.Button>
            <Radio.Button value="weekly">Chosen days</Radio.Button>
          </Radio.Group>
        </Form.Item>

        {recurrenceType === "weekly" && (
          <Form.Item
            label="Days"
            name="days_of_week"
            rules={[
              {
                validator: (_, value) =>
                  value && value.length > 0
                    ? Promise.resolve()
                    : Promise.reject(new Error("Select at least one day")),
              },
            ]}
          >
            <Checkbox.Group options={WEEKDAYS} />
          </Form.Item>
        )}

        <Form.Item
          label="Title"
          name="title"
          rules={[{ required: true, message: "Enter a title" }]}
        >
          <Input maxLength={60} placeholder="Good morning!" />
        </Form.Item>

        <Form.Item
          label="Message"
          name="message"
          rules={[{ required: true, message: "Enter a message" }]}
        >
          <TextArea
            rows={2}
            maxLength={160}
            placeholder="Time to wake up and get ready for school."
          />
        </Form.Item>

        <Form.Item label="Alarm tone">
          <Space wrap>
            <Upload
              customRequest={handleAudioUpload}
              showUploadList={false}
              accept="audio/*"
            >
              <Button loading={uploading}>
                {audio ? "Replace file" : "Upload file"}
              </Button>
            </Upload>

            {recorder.isRecording ? (
              <Button danger onClick={recorder.stop}>
                Stop recording · {formatSeconds(recorder.seconds)}
              </Button>
            ) : (
              <Button
                onClick={recorder.start}
                loading={recorder.isProcessing || savingVoice}
              >
                {voice ? "Record again" : "Record voice"}
              </Button>
            )}
          </Space>

          {recorder.isRecording && (
            <div className="mt-2 text-xs text-gray-500">
              Recording… stops automatically at {MAX_RECORDING_SECONDS} seconds.
            </div>
          )}

          {recorder.error && (
            <Alert
              className="mt-2"
              type="error"
              showIcon
              message={recorder.error}
            />
          )}

          {/* Unsaved take — preview before committing it. */}
          {recorder.result && !recorder.isRecording && (
            <div className="mt-3 p-3 rounded border border-gray-200 bg-gray-50">
              <div className="text-xs text-gray-500 mb-1">
                New recording — listen, then keep or discard.
              </div>
              <audio
                controls
                src={recorder.result.url}
                style={{ width: "100%" }}
              />
              <Space className="mt-2">
                <Button
                  type="primary"
                  size="small"
                  loading={savingVoice}
                  onClick={handleSaveRecording}
                >
                  Use this recording
                </Button>
                <Button size="small" onClick={recorder.reset}>
                  Discard
                </Button>
              </Space>
            </div>
          )}

          {(audio || voice) && (
            <div className="mt-3 space-y-2">
              {audio && (
                <TonePreview
                  label="Uploaded file"
                  tone={audio}
                  active={activeTone === "audio"}
                  onRemove={() => setAudio(null)}
                />
              )}
              {voice && (
                <TonePreview
                  label="Recorded voice"
                  tone={voice}
                  active={activeTone === "voice"}
                  onRemove={() => setVoice(null)}
                />
              )}
            </div>
          )}

          {!audio && !voice && !recorder.result && (
            <div className="mt-2 text-xs text-gray-500">
              No tone set — the alarm will ring with the phone's default sound.
            </div>
          )}

          {audio && voice && (
            <div className="mt-2 text-xs text-gray-500">
              Both are saved, but only the uploaded file rings. Remove it to use
              the recording instead.
            </div>
          )}
        </Form.Item>

        <div className="grid grid-cols-2 gap-4">
          <Form.Item label="Snooze length (minutes)" name="snooze_minutes">
            <InputNumber min={1} max={30} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item label="Snoozes allowed" name="max_snoozes">
            <InputNumber min={0} max={10} style={{ width: "100%" }} />
          </Form.Item>
        </div>

        <Form.Item
          label="Send to"
          name="target_roles"
          rules={[
            {
              validator: (_, value) =>
                value && value.length > 0
                  ? Promise.resolve()
                  : Promise.reject(new Error("Choose at least one recipient")),
            },
          ]}
        >
          <Checkbox.Group
            options={[
              { label: "Students", value: "student" },
              { label: "Parents", value: "parent" },
            ]}
          />
        </Form.Item>

        <Form.Item
          label="Skip on school holidays"
          name="skip_on_holiday"
          valuePropName="checked"
          extra="Skips school holidays and Sundays. An 'Every day' alarm with this on will not ring on a Sunday."
        >
          <Switch />
        </Form.Item>

        <Form.Item
          label="Active"
          name="is_active"
          valuePropName="checked"
          extra="Off means the alarm is saved but nobody is woken."
        >
          <Switch />
        </Form.Item>

        <Alert
          type="info"
          showIcon
          message="Families can mute this alarm"
          description="Parents and students each get an on/off switch in the mobile app's Settings. You control the time, days and tone; they can only opt out."
        />
      </Form>
    </Modal>
  );
};

export default AddEditAlarmModal;
